
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import TermsRepository from "../../repositories/implementations/TermsRepository";
import TranslationsRepository from "../../repositories/implementations/TranslationsRepository";
import { Project } from "../../entities/Project";
import { ProjectLanguage } from "../../entities/ProjectLanguage";
import { Term } from "../../entities/Term";
import { Translation } from "../../entities/Translation";
import { getFlagCode } from "../../utils/languageUtils";
import { toastSuccess, toastError } from "../../utils/toast";

@Component
export default class TermsTab extends Vue {
  @Prop({ required: true }) project!: Project;

  @Prop({ required: true }) languages!: ProjectLanguage[];

  loading = false;

  terms: Term[] = [];

  totalCount = 0;

  // termId → Set<languageCode> of complete translations
  completedByTerm: Map<string, Set<string>> = new Map();

  selectedIds: string[] = [];

  allSelected = false;

  localSearch = "";

  pageSize = 10;

  currentPage = 1;

  pageSizeOptions = [10, 50, 100];

  searchTimer: ReturnType<typeof setTimeout> | null = null;

  expandedId: string | null = null;

  loadingExpanded = false;

  // languageCode → Translation
  expandedTranslations: Record<string, Translation> = {};

  // key for showing inline save indicator
  savingKey = "";

  editingKeyId: string | null = null;

  editingKeyValue = "";

  addTermModal = { open: false, key: "", context: "", isPlural: false, keyError: "", loading: false };

  editTermModal = { open: false, termId: "", key: "", context: "", isPlural: false, keyError: "", loading: false };

  bulkDeleteModal = { open: false, loading: false };

  // Pending plural values during partial edit
  pluralPending: Record<string, { one: string | null; other: string | null }> = {};

  termsRepo = new TermsRepository();

  translationsRepo = new TranslationsRepository();

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  async created() {
    await this.loadTerms();
  }

  async loadTerms() {
    this.loading = true;
    try {
      const { terms, total } = await this.termsRepo.getByProjectPaginated(
        this.project.id,
        this.currentPage,
        this.pageSize,
        this.localSearch || undefined
      );
      this.terms = terms;
      this.totalCount = total;
      if (terms.length > 0) {
        const translations = await this.translationsRepo.getForTermIds(terms.map((t) => t.id));
        this.buildCompletedMap(terms, translations);
      } else {
        this.completedByTerm = new Map();
      }
    } finally {
      this.loading = false;
    }
  }

  async prevPage() {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      await this.loadTerms();
    }
  }

  async nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      await this.loadTerms();
    }
  }

  buildCompletedMap(terms: Term[], translations: Translation[]) {
    const pluralMap = new Map(terms.map((t) => [t.id, t.isPlural]));
    const map = new Map<string, Set<string>>();
    translations.forEach((tr) => {
      const isPlural = pluralMap.get(tr.termId) ?? false;
      if (tr.isComplete(isPlural)) {
        if (!map.has(tr.termId)) map.set(tr.termId, new Set());
        (map.get(tr.termId) as any).add(tr.languageCode);
      }
    });
    this.completedByTerm = map;
  }

  termPercent(termId: string): number {
    if (this.languages.length === 0) return 0;
    const completed = this.completedByTerm.get(termId)?.size ?? 0;
    return Math.round((completed / this.languages.length) * 100);
  }

  progressColor(percent: number): string {
    if (percent >= 80) return "success";
    if (percent >= 40) return "warning";
    return "primary";
  }

  flagCode(code: string): string {
    return getFlagCode(code);
  }

  toggleSelectAll(val: boolean) {
    this.selectedIds = val ? this.terms.map((t) => t.id) : [];
  }

  @Watch("selectedIds")
  onSelectedChanged() {
    this.allSelected = this.terms.length > 0 && this.selectedIds.length === this.terms.length;
  }

  @Watch("localSearch")
  onLocalSearchChanged() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(async () => {
      this.currentPage = 1;
      await this.loadTerms();
    }, 300);
  }

  @Watch("pageSize")
  async onPageSizeChanged() {
    this.currentPage = 1;
    await this.loadTerms();
  }

  // ── Expand ──────────────────────────────────────────────

  async toggleExpand(termId: string) {
    if (this.expandedId === termId) {
      this.expandedId = null;
      return;
    }
    this.expandedId = termId;
    this.loadingExpanded = true;
    this.expandedTranslations = {};
    this.pluralPending = {};
    try {
      const translations = await this.translationsRepo.getByTermId(termId);
      const map: Record<string, Translation> = {};
      translations.forEach((tr) => {
        map[tr.languageCode] = tr;
      });
      this.expandedTranslations = map;
    } finally {
      this.loadingExpanded = false;
    }
  }

  // ── Inline key edit ────────────────────────────────────

  startKeyEdit(term: Term) {
    this.editingKeyId = term.id;
    this.editingKeyValue = term.key;
    this.$nextTick(() => {
      const ref = this.$refs[`keyInput-${term.id}`] as any;
      if (ref) {
        const input = Array.isArray(ref) ? ref[0] : ref;
        input?.$el?.querySelector("input")?.focus();
      }
    });
  }

  cancelKeyEdit() {
    this.editingKeyId = null;
    this.editingKeyValue = "";
  }

  async saveKeyEdit(term: Term) {
    const newKey = this.editingKeyValue.trim();
    this.editingKeyId = null;
    if (!newKey || newKey === term.key) return;
    try {
      const updated = await this.termsRepo.update(term.id, newKey, term.isPlural, term.context);
      const idx = this.terms.findIndex((t) => t.id === term.id);
      if (idx !== -1) this.$set(this.terms, idx, updated);
      this.terms = [...this.terms].sort((a, b) => a.key.localeCompare(b.key));
      this.$emit("terms-changed");
    } catch (e: any) {
      toastError(e.message || "Failed to update key.");
    }
  }

  // ── Expanded translation save ──────────────────────────

  async saveExpandedTranslation(term: Term, langCode: string, valueOne: string, valueOther: string | null) {
    const trimmed = valueOne.trim();
    if (!trimmed) return;
    const existing = this.expandedTranslations[langCode];
    if (existing && trimmed === existing.valueOne?.trim() && valueOther === existing.valueOther) return;
    const key = `${term.id}:${langCode}`;
    this.savingKey = key;
    try {
      const saved = await this.translationsRepo.upsert(term.id, langCode, trimmed, valueOther);
      this.$set(this.expandedTranslations, langCode, saved);
      this.$emit("translations-updated");
      this.buildTermProgress(term);
    } catch (e: any) {
      toastError("Save failed.");
    } finally {
      if (this.savingKey === key) this.savingKey = "";
    }
  }

  async saveExpandedPluralOne(term: Term, langCode: string, valueOne: string) {
    const existing = this.expandedTranslations[langCode];
    const other = existing?.valueOther ?? this.pluralPending[`${term.id}:${langCode}`]?.other ?? null;
    if (!this.pluralPending[`${term.id}:${langCode}`]) {
      this.pluralPending[`${term.id}:${langCode}`] = { one: null, other: null };
    }
    this.pluralPending[`${term.id}:${langCode}`].one = valueOne.trim();
    const { one } = this.pluralPending[`${term.id}:${langCode}`];
    if (one && other !== null) {
      await this.saveExpandedTranslation(term, langCode, one, other);
    } else if (one && !term.isPlural) {
      await this.saveExpandedTranslation(term, langCode, one, null);
    }
  }

  async saveExpandedPluralOther(term: Term, langCode: string, valueOther: string) {
    const existing = this.expandedTranslations[langCode];
    const one = existing?.valueOne ?? this.pluralPending[`${term.id}:${langCode}`]?.one ?? null;
    if (!this.pluralPending[`${term.id}:${langCode}`]) {
      this.pluralPending[`${term.id}:${langCode}`] = { one: null, other: null };
    }
    this.pluralPending[`${term.id}:${langCode}`].other = valueOther.trim();
    const { other } = this.pluralPending[`${term.id}:${langCode}`];
    if (one && other !== null) {
      await this.saveExpandedTranslation(term, langCode, one, other);
    }
  }

  async buildTermProgress(term: Term) {
    const translations = await this.translationsRepo.getByTermId(term.id);
    const completed = new Set<string>();
    translations.forEach((tr) => {
      if (tr.isComplete(term.isPlural)) completed.add(tr.languageCode);
    });
    this.completedByTerm.set(term.id, completed);
    // Force reactivity
    this.completedByTerm = new Map(this.completedByTerm);
  }

  // ── Add term ────────────────────────────────────────────

  openAddTermModal() {
    this.addTermModal = { open: true, key: "", context: "", isPlural: false, keyError: "", loading: false };
  }

  async submitAddTerm() {
    if (!this.addTermModal.key.trim()) {
      this.addTermModal.keyError = "Key is required.";
      return;
    }
    this.addTermModal.loading = true;
    try {
      await this.termsRepo.create(this.project.id, this.addTermModal.key, this.addTermModal.isPlural, this.addTermModal.context || null);
      toastSuccess("Term created.");
      this.addTermModal.open = false;
      await this.loadTerms();
      this.$emit("terms-changed");
    } catch (e: any) {
      this.addTermModal.keyError = e.message || "Failed to create term.";
    } finally {
      this.addTermModal.loading = false;
    }
  }

  // ── Edit term ───────────────────────────────────────────

  openEditTermModal(term: Term) {
    this.editTermModal = {
      open: true,
      termId: term.id,
      key: term.key,
      context: term.context || "",
      isPlural: term.isPlural,
      keyError: "",
      loading: false,
    };
  }

  async submitEditTerm() {
    if (!this.editTermModal.key.trim()) {
      this.editTermModal.keyError = "Key is required.";
      return;
    }
    this.editTermModal.loading = true;
    try {
      const updated = await this.termsRepo.update(
        this.editTermModal.termId,
        this.editTermModal.key,
        this.editTermModal.isPlural,
        this.editTermModal.context || null
      );
      const idx = this.terms.findIndex((t) => t.id === this.editTermModal.termId);
      if (idx !== -1) this.$set(this.terms, idx, updated);
      this.terms = [...this.terms].sort((a, b) => a.key.localeCompare(b.key));
      toastSuccess("Term updated.");
      this.editTermModal.open = false;
      this.$emit("terms-changed");
    } catch (e: any) {
      this.editTermModal.keyError = e.message || "Failed to update term.";
    } finally {
      this.editTermModal.loading = false;
    }
  }

  // ── Bulk delete ─────────────────────────────────────────

  openBulkDelete() {
    this.bulkDeleteModal = { open: true, loading: false };
  }

  async submitBulkDelete() {
    this.bulkDeleteModal.loading = true;
    try {
      const count = this.selectedIds.length;
      await this.termsRepo.softDeleteMany(this.selectedIds);
      this.selectedIds = [];
      this.bulkDeleteModal.open = false;
      toastSuccess(`${count} ${count === 1 ? "term" : "terms"} deleted.`);
      // If deleting empties the current page, step back
      if (this.terms.length <= count && this.currentPage > 1) {
        this.currentPage -= 1;
      }
      await this.loadTerms();
      this.$emit("terms-changed");
    } catch (e: any) {
      toastError(e.message || "Failed to delete terms.");
    } finally {
      this.bulkDeleteModal.loading = false;
    }
  }
}
